import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminStats,
  adminDossierStats,
  adminActivity,
  adminSeedSync,
  clearAdminToken,
  getAdminToken,
} from "@/api";
import { ADMIN } from "@/constants/testIds";
import AsciiDivider from "@/components/AsciiDivider";

const formatTime = (iso) => {
  if (!iso) return "—";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return iso;
  }
};

const formatAbs = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const Tile = ({ k, label, value, sub, accent, danger }) => (
  <div
    data-testid={ADMIN.statsTile(k)}
    className="term-card admin-tile"
    style={{ margin: 0 }}
  >
    <div className="kbd-label">{label}</div>
    <div
      className="admin-tile-value"
      style={{
        color: danger
          ? "var(--ax-danger)"
          : accent
          ? "var(--ax-accent)"
          : "var(--ax-text)",
      }}
    >
      {value}
    </div>
    {sub && (
      <div className="faint" style={{ fontSize: 11, marginTop: 2 }}>
        {sub}
      </div>
    )}
  </div>
);

const FunnelRow = ({ k, label, count, pct, max = 36 }) => {
  const fillCount = Math.max(0, Math.min(max, Math.round((pct / 100) * max)));
  const empty = max - fillCount;
  const pctLabel = isNaN(pct) ? "—" : `${pct.toFixed(1)}%`;
  return (
    <div data-testid={ADMIN.funnelRow(k)} className="funnel-row">
      <span className="funnel-label">{label}</span>
      <span className="funnel-count">[{count}]</span>
      <span className="funnel-bar">
        <span className="funnel-fill">{"█".repeat(fillCount)}</span>
        <span className="funnel-empty">{"░".repeat(empty)}</span>
      </span>
      <span className="funnel-pct">{pctLabel}</span>
    </div>
  );
};

const EVENT_COLORS = {
  USER_REGISTERED: "var(--ax-text-dim)",
  DOSSIER_VIEWED: "var(--ax-accent-dim)",
  PAYWALL_HIT: "var(--ax-warn)",
  CREDIBILITY_VOTED: "var(--ax-accent)",
  EVIDENCE_SUBMITTED: "var(--ax-accent)",
  EVIDENCE_PROMOTED: "var(--ax-accent)",
  COMMENT_POSTED: "var(--ax-text-dim)",
  SUBSCRIPTION_STARTED: "var(--ax-accent)",
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [dossiers, setDossiers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [seedBusy, setSeedBusy] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  const runSeedSync = async () => {
    setSeedBusy(true);
    setSeedResult(null);
    try {
      const data = await adminSeedSync();
      setSeedResult({ ok: true, ...data });
    } catch (e) {
      setSeedResult({
        ok: false,
        message: e?.response?.data?.detail || "Sync failed.",
      });
    } finally {
      setSeedBusy(false);
    }
  };

  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin");
      return;
    }
    (async () => {
      try {
        const [s, d, a] = await Promise.all([
          adminStats(),
          adminDossierStats(),
          adminActivity(50),
        ]);
        setStats(s);
        setDossiers(d);
        setActivity(a);
      } catch (e) {
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          clearAdminToken();
          navigate("/admin");
          return;
        }
        setErr("Could not load admin data.");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const logout = () => {
    clearAdminToken();
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="term-container">
        <p className="muted">$ loading admin<span className="cursor" /></p>
      </div>
    );
  }
  if (err) {
    return (
      <div className="term-container">
        <p style={{ color: "var(--ax-danger)" }}>{err}</p>
      </div>
    );
  }

  const conversionLabel =
    stats.conversion_rate === null || stats.conversion_rate === undefined
      ? "—"
      : `${stats.conversion_rate.toFixed(1)}%`;

  const mrrLabel =
    stats.mrr && stats.mrr > 0
      ? `$${stats.mrr.toFixed(2)}`
      : "$0.00";

  // Funnel percentages anchored to total_users
  const total = stats.total_users || 0;
  const pct = (n) => (total > 0 ? (n / total) * 100 : 0);
  // "Retained 30d" proxy: paid users whose expiry is > now + something is fine,
  // but for a real "retained 30d" we'd need cohort data. Use paid_subscribers
  // as a placeholder since we don't (yet) track cohort retention.
  const retained30 = stats.paid_subscribers;

  return (
    <div className="term-container fade-in" data-testid={ADMIN.dashboardRoot}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <div>
          <div className="kbd-label">// moderation console</div>
          <h1 style={{ margin: "0.2rem 0 0", color: "var(--ax-accent)" }}>
            ADMIN
          </h1>
        </div>
        <button className="ghost" data-testid={ADMIN.logout} onClick={logout}>
          LOGOUT
        </button>
      </div>

      {/* SEED SYNC — additively inserts dossiers from seed/theories.py that
          aren't yet in the DB. Idempotent. Use this after every redeploy that
          adds a new dossier. */}
      <div
        className="term-card"
        style={{ marginTop: "1rem", padding: "0.7rem 0.9rem" }}
      >
        <div
          style={{
            display: "flex",
            gap: "0.8rem",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div className="kbd-label" style={{ margin: 0 }}>
            // dossier-seed deployment
          </div>
          <button
            data-testid={ADMIN.seedSyncButton}
            onClick={runSeedSync}
            disabled={seedBusy}
            style={{ padding: "0.25rem 0.8rem", fontSize: 12 }}
          >
            {seedBusy ? "SYNCING…" : "↻ SYNC SEED DOSSIERS"}
          </button>
          {seedResult && (
            <span
              data-testid={ADMIN.seedSyncResult}
              style={{
                fontSize: 12,
                color: seedResult.ok
                  ? "var(--ax-accent)"
                  : "var(--ax-danger)",
              }}
            >
              {seedResult.ok
                ? seedResult.inserted.length === 0 && seedResult.updated.length === 0
                  ? `// already in sync · ${seedResult.total_in_db_after}/${seedResult.total_in_seed} dossiers`
                  : `// inserted: [${seedResult.inserted.join(", ") || "none"}] · updated: [${seedResult.updated.map((u) => `${u.slug} (${u.fields_changed.join("+")})`).join(", ") || "none"}] · now ${seedResult.total_in_db_after}/${seedResult.total_in_seed}`
                : `// ${seedResult.message}`}
            </span>
          )}
        </div>
      </div>

      {/* TILE GRID — 2 rows of 4 */}
      <div
        data-testid={ADMIN.statsCard}
        className="admin-tile-grid"
        style={{ marginTop: "1.5rem" }}
      >
        <Tile
          k="total-users"
          label="TOTAL USERS"
          value={stats.total_users}
        />
        <Tile
          k="new-today"
          label="NEW TODAY"
          value={stats.new_today}
          sub="last 24h"
        />
        <Tile
          k="new-week"
          label="NEW THIS WEEK"
          value={stats.new_this_week}
          sub="last 7d"
        />
        <Tile
          k="active-today"
          label="ACTIVE TODAY"
          value={stats.active_today}
          sub="any action · last 24h"
        />
        <Tile
          k="paywall-hits"
          label="PAYWALL HITS"
          value={stats.paywall_hits}
          sub="users at the gate"
          danger
        />
        <Tile
          k="conversion-rate"
          label="CONVERSION RATE"
          value={conversionLabel}
          sub="paid ÷ paywall hits"
          accent={
            stats.conversion_rate !== null && stats.conversion_rate >= 1.0
          }
        />
        <Tile
          k="paid-subs"
          label="PAID SUBS"
          value={stats.paid_subscribers}
          sub={`${stats.monthly_subscribers}mo · ${stats.yearly_subscribers}yr`}
          accent
        />
        <Tile
          k="mrr"
          label="MRR"
          value={mrrLabel}
          sub={
            stats.mrr === 0
              ? "$0.00 until Stripe live"
              : `lifetime: $${(stats.total_revenue_usd ?? 0).toFixed(2)}`
          }
          accent={stats.mrr > 0}
        />
      </div>

      <AsciiDivider />

      {/* SECTION 1: TOP DOSSIERS */}
      <div className="kbd-label" style={{ margin: "1.4rem 0 0.6rem" }}>
        $ stat /dossiers --last=7d
      </div>
      <div data-testid={ADMIN.topDossiers} className="term-card" style={{ padding: 0 }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>DOSSIER</th>
              <th style={{ textAlign: "right" }}>VIEWS</th>
              <th style={{ textAlign: "right" }}>UNIQUE VIEWERS</th>
              <th style={{ textAlign: "right" }}>AVG CREDIBILITY</th>
              <th style={{ textAlign: "right" }}>EVIDENCE</th>
            </tr>
          </thead>
          <tbody>
            {dossiers.map((d) => (
              <tr
                key={d.slug}
                data-testid={ADMIN.topDossierRow(d.slug)}
              >
                <td className="muted" style={{ whiteSpace: "nowrap" }}>
                  {d.title}
                </td>
                <td style={{ textAlign: "right" }} className="glow-dim">
                  {d.views}
                </td>
                <td style={{ textAlign: "right" }}>{d.unique_viewers}</td>
                <td style={{ textAlign: "right" }}>
                  {d.avg_credibility == null ? "—" : `${d.avg_credibility.toFixed(1)}/100`}
                </td>
                <td style={{ textAlign: "right" }}>{d.evidence_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AsciiDivider />

      {/* SECTION 2: CONVERSION FUNNEL */}
      <div className="kbd-label" style={{ margin: "1.4rem 0 0.6rem" }}>
        $ stat /funnel --live
      </div>
      <div data-testid={ADMIN.funnel} className="term-card" style={{ padding: "1rem 1.2rem" }}>
        <FunnelRow
          k="registered"
          label="REGISTERED USERS"
          count={total}
          pct={total > 0 ? 100 : 0}
        />
        <FunnelRow
          k="paywall"
          label="HIT PAYWALL"
          count={stats.paywall_hits}
          pct={pct(stats.paywall_hits)}
        />
        <FunnelRow
          k="subscribed"
          label="SUBSCRIBED"
          count={stats.paid_subscribers}
          pct={pct(stats.paid_subscribers)}
        />
        <FunnelRow
          k="retained"
          label="RETAINED 30d"
          count={retained30}
          pct={pct(retained30)}
        />
        <div className="faint" style={{ fontSize: 11, marginTop: "0.5rem" }}>
          retention proxy = current paid subs (true cohort retention requires
          historical snapshots — flagged in backlog)
        </div>
      </div>

      <AsciiDivider />

      {/* SECTION 3: RECENT ACTIVITY LOG */}
      <div className="kbd-label" style={{ margin: "1.4rem 0 0.6rem" }}>
        $ tail -n 50 /var/log/axiom.activity
      </div>
      <div data-testid={ADMIN.activityLog} className="term-card" style={{ padding: 0 }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>WHEN</th>
              <th>CODENAME</th>
              <th>EVENT</th>
              <th>TARGET</th>
              <th>DETAIL</th>
            </tr>
          </thead>
          <tbody>
            {activity.map((e, i) => (
              <tr key={`${e.when}-${i}`} data-testid={ADMIN.activityRow(i)}>
                <td
                  className="faint"
                  style={{ whiteSpace: "nowrap" }}
                  title={formatAbs(e.when)}
                >
                  {formatTime(e.when)}
                </td>
                <td className="muted" style={{ whiteSpace: "nowrap" }}>
                  {e.codename}
                </td>
                <td
                  style={{
                    whiteSpace: "nowrap",
                    color: EVENT_COLORS[e.event_type] || "var(--ax-text)",
                    fontWeight: 700,
                    fontSize: 11,
                    letterSpacing: "0.05em",
                  }}
                >
                  {e.event_type}
                </td>
                <td className="muted" style={{ whiteSpace: "nowrap" }}>
                  {e.target}
                </td>
                <td
                  className="faint"
                  style={{
                    maxWidth: 320,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {e.detail}
                </td>
              </tr>
            ))}
            {activity.length === 0 && (
              <tr>
                <td colSpan={5} className="muted" style={{ padding: "1rem 0.8rem" }}>
                  $ no activity recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminDashboard;
